import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  Minus,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  TrendingDown,
  TrendingUp,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { ApiClientError, api } from "../lib/api";
import { dateOnly, integer, money, whenever } from "../lib/format";
import i18n from "../lib/i18n";
import { cn } from "../lib/utils";

type Marketplace = "shopify" | "amazon" | "walmart" | "ebay" | "tiktok_shop" | "wholesale";
type Unit = "stickers" | "figures" | "electronics";
type OrderStatus =
  | "pending"
  | "unshipped"
  | "partially_shipped"
  | "shipped"
  | "delivered"
  | "cancelled"
  | "refunded";

interface ChannelRow {
  id: string;
  marketplace: Marketplace;
  accountLabel: string;
}

interface OrderRow {
  id: string;
  channelId: string;
  marketplace: Marketplace;
  accountLabel: string;
  externalOrderId: string;
  orderNumber: string;
  status: OrderStatus;
  currency: string;
  total: string;
  buyerName: string | null;
  shippingCity: string | null;
  shippingState: string | null;
  shippingCountry: string | null;
  orderedAt: string;
  lastSyncedAt: string | null;
  itemCount: number;
  unmatchedLineCount: number;
}

interface OrderLineItemRow {
  id: string;
  productId: string | null;
  sku: string | null;
  channelSku: string;
  title: string;
  quantity: number;
  unitPrice: string | null;
  lineTotal: string | null;
}

interface OrderDetail extends OrderRow {
  subtotal: string | null;
  shippingTotal: string | null;
  taxTotal: string | null;
  buyerEmail: string | null;
  shippingLine1: string | null;
  shippingLine2: string | null;
  shippingPostalCode: string | null;
  lineItems: OrderLineItemRow[];
}

interface OrdersSummary {
  totalOrders: number;
  totalRevenue: string;
  unshippedCount: number;
  unmatchedLines: number;
}

interface OrderSyncSummary {
  marketplace: string;
  fetched: number;
  linesMatched: number;
  linesUnmatched: number;
}

type KpiPeriod = "7d" | "30d" | "60d" | "90d" | "180d";

interface KpiTotals {
  revenue: string;
  orders: number;
  avgOrderValue: string;
}

interface KpiSeriesPoint {
  date: string;
  revenue: string;
  orders: number;
}

interface TopItem {
  sku: string | null;
  channelSku: string;
  title: string;
  unitsSold: number;
  revenue: string;
}

interface KpiChannelTotals {
  channelId: string;
  revenue: string;
  orders: number;
}

interface KpiData {
  period: KpiPeriod;
  channelId: string | null;
  current: KpiTotals;
  previous: KpiTotals;
  revenueChangePct: number | null;
  ordersChangePct: number | null;
  byChannel: KpiChannelTotals[];
  series: KpiSeriesPoint[];
  topItems: TopItem[];
}

/** Shared shape of GET /invoices/kpis and GET /local-sales/kpis — the two
 * non-channel revenue sources that make up "Total revenue" alongside
 * KpiData above. */
interface RevenueSummary {
  period: KpiPeriod;
  current: { revenue: string; count: number; avgSale: string };
  previous: { revenue: string; count: number; avgSale: string };
  revenueChangePct: number | null;
  countChangePct: number | null;
  series: Array<{ date: string; revenue: string; count: number }>;
  topItems: Array<{ description: string; quantitySold: number; revenue: string }>;
}

interface LocalSaleRow {
  id: string;
  soldAt: string;
  description: string;
  quantity: string;
  unitPrice: string;
  amount: string;
  currency: string;
  soldBy: string | null;
  notes: string | null;
}

const ORDER_STATUSES: OrderStatus[] = [
  "pending",
  "unshipped",
  "partially_shipped",
  "shipped",
  "delivered",
  "cancelled",
  "refunded",
];

const STATUS_VARIANT: Record<
  OrderStatus,
  "secondary" | "info" | "success" | "destructive" | "outline"
> = {
  pending: "secondary",
  unshipped: "info",
  partially_shipped: "info",
  shipped: "success",
  delivered: "success",
  cancelled: "destructive",
  refunded: "outline",
};

const INVENTORY_UNITS: Unit[] = ["stickers", "figures", "electronics"];
const KPI_PERIODS: KpiPeriod[] = ["7d", "30d", "60d", "90d", "180d"];
/** Same set as server/lib/currency.ts's KNOWN_CURRENCIES (+ USD) -- kept as
 * its own short list here rather than fetched, matching that file's own
 * "small known list, extend when a new one shows up" approach. */
const KNOWN_CURRENCIES = ["USD", "CAD", "MXN", "BRL"];

export default function Sales() {
  const { t } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const qc = useQueryClient();

  const channelsQ = useQuery({
    queryKey: ["channels"],
    queryFn: () => api<ChannelRow[]>("/api/v1/channels"),
  });
  const summaryQ = useQuery({
    queryKey: ["orders-summary"],
    queryFn: () => api<OrdersSummary>("/api/v1/orders/summary"),
  });

  const [kpiPeriod, setKpiPeriod] = useState<KpiPeriod>("30d");
  const [kpiChannelFilter, setKpiChannelFilter] = useState<string>("all");
  const kpiQ = useQuery({
    queryKey: ["orders-kpis", kpiPeriod, kpiChannelFilter],
    queryFn: () => {
      const params = new URLSearchParams({ period: kpiPeriod });
      if (kpiChannelFilter !== "all") params.set("channelId", kpiChannelFilter);
      return api<KpiData>(`/api/v1/orders/kpis?${params.toString()}`);
    },
  });

  // The other two revenue sources that make up "Total revenue" alongside
  // channel orders above — same period selector, independent queries so a
  // slow/failing one (e.g. no invoices scope granted) doesn't block the
  // channel numbers this page already relied on.
  const invoiceKpiQ = useQuery({
    queryKey: ["invoice-kpis", kpiPeriod],
    queryFn: () => api<RevenueSummary>(`/api/v1/invoices/kpis?period=${kpiPeriod}`),
  });
  const localSaleKpiQ = useQuery({
    queryKey: ["local-sale-kpis", kpiPeriod],
    queryFn: () => api<RevenueSummary>(`/api/v1/local-sales/kpis?period=${kpiPeriod}`),
  });

  const [syncResults, setSyncResults] = useState<Record<string, OrderSyncSummary>>({});
  const [syncErrors, setSyncErrors] = useState<Record<string, string>>({});
  const [syncAllToast, setSyncAllToast] = useState<"success" | "error" | null>(null);
  // Which channels a "Sync all channels" run is still waiting on — Amazon's
  // own order sync alone can take several minutes (it's an async Reports-API
  // submit/poll/download cycle, repeated per 29-day chunk of the pull
  // window), and with no per-channel signal while the batch is in flight the
  // whole page looked identical for that entire time, indistinguishable from
  // being stuck. This drives a live "N of M done" count and lets each
  // channel's own card update the moment THAT channel finishes, instead of
  // every card going stale until the slowest one completes.
  const [batchSyncingIds, setBatchSyncingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!syncAllToast) return;
    const timer = setTimeout(() => setSyncAllToast(null), 5000);
    return () => clearTimeout(timer);
  }, [syncAllToast]);

  const invalidateOrders = () => {
    void qc.invalidateQueries({ queryKey: ["orders"] });
    void qc.invalidateQueries({ queryKey: ["orders-summary"] });
    void qc.invalidateQueries({ queryKey: ["orders-kpis"] });
  };

  const syncOrdersMutation = useMutation({
    mutationFn: (channelId: string) =>
      api<OrderSyncSummary>(`/api/v1/channels/${channelId}/sync-orders`, { method: "POST" }),
    onSuccess: (summary, channelId) => {
      setSyncResults((prev) => ({ ...prev, [channelId]: summary }));
      setSyncErrors((prev) => {
        const next = { ...prev };
        delete next[channelId];
        return next;
      });
      invalidateOrders();
    },
    onError: (err, channelId) => {
      setSyncErrors((prev) => ({
        ...prev,
        [channelId]: err instanceof ApiClientError ? err.message : t("sales:syncFailed"),
      }));
    },
  });

  const syncAllOrdersMutation = useMutation({
    mutationFn: async () => {
      setBatchSyncingIds(new Set(channels.map((c) => c.id)));
      // Each channel's own promise updates syncResults/syncErrors as soon as
      // THAT channel settles, rather than all cards waiting for
      // Promise.allSettled to resolve every one of them — see
      // batchSyncingIds' comment above for why that matters here.
      const results = await Promise.allSettled(
        channels.map((c) =>
          api<OrderSyncSummary>(`/api/v1/channels/${c.id}/sync-orders`, { method: "POST" })
            .then((summary) => {
              setSyncResults((prev) => ({ ...prev, [c.id]: summary }));
              setSyncErrors((prev) => {
                const next = { ...prev };
                delete next[c.id];
                return next;
              });
              return { channelId: c.id, summary } as const;
            })
            .catch((err) => {
              setSyncErrors((prev) => ({
                ...prev,
                [c.id]: err instanceof ApiClientError ? err.message : t("sales:syncFailed"),
              }));
              throw err;
            })
            .finally(() => {
              setBatchSyncingIds((prev) => {
                const next = new Set(prev);
                next.delete(c.id);
                return next;
              });
            }),
        ),
      );
      return results;
    },
    onSuccess: (results) => {
      invalidateOrders();
      setSyncAllToast(results.every((r) => r.status === "fulfilled") ? "success" : "error");
    },
    onError: () => {
      setSyncAllToast("error");
    },
  });

  /* ─────────────────────────── filters ─────────────────────────── */

  const [channelFilter, setChannelFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<OrderStatus | "all">("all");
  const [unitFilter, setUnitFilter] = useState<Unit | "all">("all");
  const [currencyFilter, setCurrencyFilter] = useState<string>("all");
  const [term, setTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [limit, setLimit] = useState(50);

  const params = new URLSearchParams({ limit: String(limit) });
  if (channelFilter !== "all") params.set("channelId", channelFilter);
  if (statusFilter !== "all") params.set("status", statusFilter);
  if (unitFilter !== "all") params.set("businessUnit", unitFilter);
  if (currencyFilter !== "all") params.set("currency", currencyFilter);
  if (term.trim()) params.set("q", term.trim());
  if (dateFrom) params.set("from", new Date(dateFrom).toISOString());
  if (dateTo) params.set("to", new Date(dateTo).toISOString());

  const ordersQ = useQuery({
    queryKey: [
      "orders",
      channelFilter,
      statusFilter,
      unitFilter,
      currencyFilter,
      term,
      dateFrom,
      dateTo,
      limit,
    ],
    queryFn: () => api<OrderRow[]>(`/api/v1/orders?${params.toString()}`),
  });

  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const detailQ = useQuery({
    queryKey: ["order-detail", detailOrderId],
    queryFn: () => api<OrderDetail>(`/api/v1/orders/${detailOrderId}`),
    enabled: detailOrderId !== null,
  });

  /* ─────────────────────────── local sales ─────────────────────────── */

  const localSalesQ = useQuery({
    queryKey: ["local-sales"],
    queryFn: () => api<LocalSaleRow[]>("/api/v1/local-sales?limit=15"),
  });

  const invalidateLocalSales = () => {
    void qc.invalidateQueries({ queryKey: ["local-sales"] });
    void qc.invalidateQueries({ queryKey: ["local-sale-kpis"] });
  };

  const EMPTY_LOCAL_SALE_FORM = {
    soldAt: new Date().toISOString().slice(0, 10),
    description: "",
    quantity: "1",
    // The form asks for the total amount, not a unit price — that's how the
    // sales this replaces were actually recorded (one lump amount per sale,
    // quantity only implied for a bulk lot). unitPrice is derived from the
    // two at submit time, since that's what the schema/column actually
    // store (matching invoice_line_items' own quantity+unitPrice shape).
    amount: "",
    soldBy: "",
    notes: "",
  };
  const [localSaleDialogOpen, setLocalSaleDialogOpen] = useState(false);
  const [localSaleForm, setLocalSaleForm] = useState(EMPTY_LOCAL_SALE_FORM);
  const [localSaleError, setLocalSaleError] = useState<string | null>(null);

  const createLocalSaleMutation = useMutation({
    mutationFn: () => {
      const quantity = Number(localSaleForm.quantity || "1");
      const amount = Number(localSaleForm.amount || "0");
      return api<LocalSaleRow>("/api/v1/local-sales", {
        method: "POST",
        body: JSON.stringify({
          soldAt: localSaleForm.soldAt,
          description: localSaleForm.description.trim(),
          quantity,
          unitPrice: quantity > 0 ? amount / quantity : 0,
          soldBy: localSaleForm.soldBy.trim() || undefined,
          notes: localSaleForm.notes.trim() || undefined,
        }),
      });
    },
    onSuccess: () => {
      invalidateLocalSales();
      setLocalSaleDialogOpen(false);
      setLocalSaleForm(EMPTY_LOCAL_SALE_FORM);
      setLocalSaleError(null);
    },
    onError: (err) => {
      setLocalSaleError(err instanceof ApiClientError ? err.message : t("sales:local.saveFailed"));
    },
  });

  const deleteLocalSaleMutation = useMutation({
    mutationFn: (id: string) => api(`/api/v1/local-sales/${id}`, { method: "DELETE" }),
    onSuccess: invalidateLocalSales,
  });

  const channels = channelsQ.data ?? [];
  const orderRows = ordersQ.data ?? [];
  const localSaleRows = localSalesQ.data ?? [];

  // Combined across the three independent sources — none of the three
  // queries block the others, so this degrades gracefully (shows 0, not an
  // error) if one hasn't loaded yet or the viewer lacks the invoices scope.
  const totalRevenue =
    Number(kpiQ.data?.current.revenue ?? 0) +
    Number(invoiceKpiQ.data?.current.revenue ?? 0) +
    Number(localSaleKpiQ.data?.current.revenue ?? 0);

  return (
    <>
      <div className="mb-6">
        <h1>{t("navigation:sales")}</h1>
        <p className="mt-2 text-muted-foreground">{t("sales:subtitle")}</p>
      </div>

      <section className="mb-6">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2>{t("sales:pullOrders")}</h2>
          <div className="flex items-center gap-2">
            {syncAllOrdersMutation.isPending && (
              <span role="status" className="text-sm text-muted-foreground">
                {t("sales:syncAllProgress", {
                  done: channels.length - batchSyncingIds.size,
                  total: channels.length,
                })}
              </span>
            )}
            {!syncAllOrdersMutation.isPending && syncAllToast && (
              <span
                role="status"
                className={cn(
                  "flex items-center gap-1 text-sm",
                  syncAllToast === "success" ? "text-emerald-500" : "text-destructive",
                )}
              >
                {syncAllToast === "success" ? (
                  <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
                ) : (
                  <XCircle className="h-4 w-4 shrink-0" aria-hidden />
                )}
                {syncAllToast === "success" ? t("sales:syncAllSuccess") : t("sales:syncAllError")}
              </span>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={syncAllOrdersMutation.isPending || channels.length === 0}
              onClick={() => syncAllOrdersMutation.mutate()}
            >
              <RefreshCw
                className={cn("h-3.5 w-3.5", syncAllOrdersMutation.isPending && "animate-spin")}
                aria-hidden
              />
              {t("sales:syncAllOrders")}
            </Button>
          </div>
        </div>
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {channels.map((c) => (
            <li key={c.id}>
              <Card className="h-full p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">
                    {t(`marketplaces:name.${c.marketplace}`)}
                    <span className="ml-1.5 text-xs text-muted-foreground">{c.accountLabel}</span>
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={
                      (syncOrdersMutation.isPending && syncOrdersMutation.variables === c.id) ||
                      batchSyncingIds.has(c.id)
                    }
                    onClick={() => syncOrdersMutation.mutate(c.id)}
                  >
                    <RefreshCw
                      className={cn(
                        "h-3.5 w-3.5",
                        (syncOrdersMutation.isPending && syncOrdersMutation.variables === c.id) ||
                          batchSyncingIds.has(c.id)
                          ? "animate-spin"
                          : undefined,
                      )}
                      aria-hidden
                    />
                    {t("sales:syncOrders")}
                  </Button>
                </div>
                {batchSyncingIds.has(c.id) ? (
                  <p className="mt-2 text-xs text-muted-foreground">{t("sales:syncingNow")}</p>
                ) : (
                  (() => {
                    const channelTotals = kpiQ.data?.byChannel.find((b) => b.channelId === c.id);
                    return (
                      <p className="mt-2 text-xs text-muted-foreground">
                        {channelTotals && channelTotals.orders > 0
                          ? t("sales:channelRevenue", {
                              revenue: money(channelTotals.revenue, "USD", locale),
                              count: channelTotals.orders,
                            })
                          : t("sales:channelNoOrdersYet")}
                      </p>
                    );
                  })()
                )}
                {!batchSyncingIds.has(c.id) && syncErrors[c.id] && (
                  <p className="mt-2 text-xs text-destructive">{syncErrors[c.id]}</p>
                )}
                {!batchSyncingIds.has(c.id) && syncResults[c.id] && !syncErrors[c.id] && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {t("sales:syncResult", {
                      fetched: syncResults[c.id]?.fetched ?? 0,
                      matched: syncResults[c.id]?.linesMatched ?? 0,
                    })}
                  </p>
                )}
              </Card>
            </li>
          ))}
        </ul>
      </section>

      <section className="mb-6">
        <div className="mb-1 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <h2>{t("sales:performance")}</h2>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="w-full sm:w-48">
              <Label htmlFor="kpi-channel" className="sr-only">
                {t("sales:filter.channel")}
              </Label>
              <Select value={kpiChannelFilter} onValueChange={setKpiChannelFilter}>
                <SelectTrigger id="kpi-channel" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("marketplaces:allChannels")}</SelectItem>
                  {channels.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {t(`marketplaces:name.${c.marketplace}`)} · {c.accountLabel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <fieldset className="flex flex-wrap gap-1 border-0 p-0">
              <legend className="sr-only">{t("sales:period")}</legend>
              {KPI_PERIODS.map((p) => (
                <Button
                  key={p}
                  type="button"
                  size="sm"
                  variant={kpiPeriod === p ? "default" : "ghost"}
                  onClick={() => setKpiPeriod(p)}
                >
                  {t(`sales:periods.${p}`)}
                </Button>
              ))}
            </fieldset>
          </div>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          {kpiChannelFilter === "all" ? t("sales:allChannelsCombined") : t("sales:oneChannelOnly")}
        </p>

        {kpiQ.isLoading && <p className="text-muted-foreground">{t("common:loading")}</p>}

        {kpiQ.data && (
          <>
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <KpiCard
                label={t("sales:kpi.revenue")}
                value={money(kpiQ.data.current.revenue, "USD", locale)}
                changePct={kpiQ.data.revenueChangePct}
              />
              <KpiCard
                label={t("sales:kpi.orders")}
                value={integer(kpiQ.data.current.orders, locale)}
                changePct={kpiQ.data.ordersChangePct}
              />
              <KpiCard
                label={t("sales:kpi.aov")}
                value={money(kpiQ.data.current.avgOrderValue, "USD", locale)}
                changePct={pctChange(
                  Number(kpiQ.data.current.avgOrderValue),
                  Number(kpiQ.data.previous.avgOrderValue),
                )}
              />
            </div>

            <Card className="mb-4 p-4">
              <p className="mb-2 text-xs text-muted-foreground">{t("sales:kpi.trend")}</p>
              {kpiQ.data.series.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  {t("sales:kpi.noTrendData")}
                </p>
              ) : (
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={kpiQ.data.series}
                      margin={{ top: 4, right: 8, left: 8, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="salesRevenueFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.35} />
                          <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="hsl(var(--border))"
                      />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(d: string) => formatTrendDate(d, locale)}
                        tickLine={false}
                        axisLine={false}
                        tick={{ fontSize: 11 }}
                        minTickGap={40}
                      />
                      <YAxis hide domain={[0, "auto"]} />
                      <RechartsTooltip content={<TrendTooltip locale={locale} />} />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke="hsl(var(--chart-1))"
                        strokeWidth={2}
                        fill="url(#salesRevenueFill)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>

            <Card>
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-base">{t("sales:kpi.topItems")}</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                {kpiQ.data.topItems.length === 0 ? (
                  <p className="py-4 text-center text-sm text-muted-foreground">
                    {t("sales:kpi.noTopItems")}
                  </p>
                ) : (
                  <ul className="divide-y">
                    {kpiQ.data.topItems.map((item, i) => (
                      <li key={item.channelSku} className="flex items-center gap-3 py-2">
                        <span className="w-5 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                          {i + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{item.title}</p>
                          <p className="truncate font-mono text-xs text-muted-foreground">
                            {item.sku ?? item.channelSku}
                          </p>
                        </div>
                        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                          {t("sales:kpi.unitsSold", { count: item.unitsSold })}
                        </span>
                        <span className="w-20 shrink-0 text-right text-sm font-medium tabular-nums">
                          {money(item.revenue, "USD", locale)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </section>

      <section className="mb-6">
        <h2 className="mb-1">{t("sales:local.totalRevenueTitle")}</h2>
        <p className="mb-3 text-xs text-muted-foreground">{t("sales:local.totalRevenueHint")}</p>

        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="p-3 pb-1">
              <CardTitle className="text-xs font-normal text-muted-foreground">
                {t("sales:local.totalRevenue")}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              <span className="text-2xl font-semibold tabular-nums">
                {money(totalRevenue.toFixed(2), "USD", locale)}
              </span>
            </CardContent>
          </Card>
          <KpiCard
            label={t("sales:local.channels")}
            value={money(kpiQ.data?.current.revenue ?? "0", "USD", locale)}
            changePct={kpiQ.data?.revenueChangePct ?? null}
          />
          <KpiCard
            label={t("sales:local.invoicedSales")}
            value={money(invoiceKpiQ.data?.current.revenue ?? "0", "USD", locale)}
            changePct={invoiceKpiQ.data?.revenueChangePct ?? null}
          />
          <KpiCard
            label={t("sales:local.localSales")}
            value={money(localSaleKpiQ.data?.current.revenue ?? "0", "USD", locale)}
            changePct={localSaleKpiQ.data?.revenueChangePct ?? null}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between p-4 pb-2">
              <CardTitle className="text-base">{t("sales:local.recentTitle")}</CardTitle>
              <Button type="button" size="sm" onClick={() => setLocalSaleDialogOpen(true)}>
                <Plus className="h-4 w-4" aria-hidden />
                {t("sales:local.record")}
              </Button>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {localSalesQ.isLoading && (
                <p className="text-sm text-muted-foreground">{t("common:loading")}</p>
              )}
              {!localSalesQ.isLoading && localSaleRows.length === 0 && (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  {t("sales:local.empty")}
                </p>
              )}
              {localSaleRows.length > 0 && (
                <ul className="divide-y">
                  {localSaleRows.map((s) => (
                    <li key={s.id} className="flex items-center gap-3 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{s.description}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {dateOnly(s.soldAt, locale)}
                          {s.soldBy ? ` · ${s.soldBy}` : ""}
                        </p>
                      </div>
                      <span className="shrink-0 text-sm font-medium tabular-nums">
                        {money(s.amount, s.currency, locale)}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t("sales:local.delete")}
                        disabled={
                          deleteLocalSaleMutation.isPending &&
                          deleteLocalSaleMutation.variables === s.id
                        }
                        onClick={() => deleteLocalSaleMutation.mutate(s.id)}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-base">{t("sales:local.itemsTitle")}</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {(() => {
                // Invoiced + local sales combined here — neither has a SKU
                // to join against the product catalogue (that's the whole
                // point of both), so there's no "top items" table that
                // could ever include them; this is the only place they're
                // visible as items rather than just a revenue number.
                const combined = [
                  ...(invoiceKpiQ.data?.topItems ?? []).map((t) => ({ ...t, source: "inv" })),
                  ...(localSaleKpiQ.data?.topItems ?? []).map((t) => ({ ...t, source: "local" })),
                ].sort((a, b) => Number(b.revenue) - Number(a.revenue));
                if (combined.length === 0) {
                  return (
                    <p className="py-4 text-center text-sm text-muted-foreground">
                      {t("sales:local.noItems")}
                    </p>
                  );
                }
                return (
                  <ul className="divide-y">
                    {combined.slice(0, 10).map((item) => (
                      <li
                        key={`${item.source}-${item.description}`}
                        className="flex items-center gap-3 py-2"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm">{item.description}</p>
                        </div>
                        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                          {t("sales:kpi.unitsSold", { count: item.quantitySold })}
                        </span>
                        <span className="w-20 shrink-0 text-right text-sm font-medium tabular-nums">
                          {money(item.revenue, "USD", locale)}
                        </span>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      </section>

      {((summaryQ.data?.unshippedCount ?? 0) > 0 || (summaryQ.data?.unmatchedLines ?? 0) > 0) && (
        <div className="mb-6 flex flex-wrap gap-3">
          {(summaryQ.data?.unshippedCount ?? 0) > 0 && (
            <Badge variant="warning" className="px-3 py-1.5 text-sm">
              {t("sales:stats.unshippedCount", { count: summaryQ.data?.unshippedCount ?? 0 })}
            </Badge>
          )}
          {(summaryQ.data?.unmatchedLines ?? 0) > 0 && (
            <Badge variant="warning" className="px-3 py-1.5 text-sm">
              {t("sales:stats.unmatchedLinesCount", { count: summaryQ.data?.unmatchedLines ?? 0 })}
            </Badge>
          )}
        </div>
      )}

      <section>
        <div className="mb-3 flex flex-col gap-2 lg:flex-row lg:items-end lg:flex-wrap">
          <div className="flex-1 min-w-[12rem]">
            <Label htmlFor="sales-search" className="sr-only">
              {t("sales:filter.searchPlaceholder")}
            </Label>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                id="sales-search"
                type="search"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder={t("sales:filter.searchPlaceholder")}
                className="pl-9"
              />
            </div>
          </div>

          <div className="w-full sm:w-44">
            <Label htmlFor="sales-channel" className="sr-only">
              {t("sales:filter.channel")}
            </Label>
            <Select value={channelFilter} onValueChange={setChannelFilter}>
              <SelectTrigger id="sales-channel" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("marketplaces:allChannels")}</SelectItem>
                {channels.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {t(`marketplaces:name.${c.marketplace}`)} · {c.accountLabel}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-full sm:w-40">
            <Label htmlFor="sales-status" className="sr-only">
              {t("sales:filter.status")}
            </Label>
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as OrderStatus | "all")}
            >
              <SelectTrigger id="sales-status" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("sales:filter.allStatuses")}</SelectItem>
                {ORDER_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {t(`sales:status.${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-full sm:w-40">
            <Label htmlFor="sales-unit" className="sr-only">
              {t("sales:filter.businessUnit")}
            </Label>
            <Select value={unitFilter} onValueChange={(v) => setUnitFilter(v as Unit | "all")}>
              <SelectTrigger id="sales-unit" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("sales:filter.allCategories")}</SelectItem>
                {INVENTORY_UNITS.map((u) => (
                  <SelectItem key={u} value={u}>
                    {t(`common:units.${u}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-full sm:w-32">
            <Label htmlFor="sales-currency" className="sr-only">
              {t("sales:filter.currency")}
            </Label>
            <Select value={currencyFilter} onValueChange={setCurrencyFilter}>
              <SelectTrigger id="sales-currency" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("sales:filter.allCurrencies")}</SelectItem>
                {KNOWN_CURRENCIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Native date inputs have a browser-enforced minimum width (~180px for
              the mm/dd/yyyy placeholder + picker icon) that can't shrink below
              that no matter what width class is applied — two side by side
              don't fit in a narrow phone viewport at all, so this only goes
              row-direction from `sm:` up. */}
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-end">
            <div className="w-full sm:w-auto">
              <Label htmlFor="sales-from" className="text-xs text-muted-foreground">
                {t("sales:filter.from")}
              </Label>
              <Input
                id="sales-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="mt-1 w-full"
              />
            </div>
            <div className="w-full sm:w-auto">
              <Label htmlFor="sales-to" className="text-xs text-muted-foreground">
                {t("sales:filter.to")}
              </Label>
              <Input
                id="sales-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="mt-1 w-full"
              />
            </div>
          </div>
        </div>

        {ordersQ.isLoading && <p className="text-muted-foreground">{t("common:loading")}</p>}

        {!ordersQ.isLoading && orderRows.length === 0 && (
          <p className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
            {t("sales:noOrders")}
          </p>
        )}

        {orderRows.length > 0 && (
          <Card>
            {/* table-fixed, same reasoning as the Unmatched SKUs table: the
                default `auto` layout sizes a column to fit its widest content,
                so a long order id just grows the column instead of truncating
                — `truncate` needs a bounded width to actually clip against,
                which only `table-fixed` + explicit header widths provide.
                No forced min-width below `sm:` — order #, status and total are
                what a scan of the grid is actually for, channel/ship-to/items/
                date fold back in from `sm:` up rather than forcing a scroll
                past what's most read. */}
            <Table className="table-fixed sm:min-w-[56rem]">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("sales:order")}</TableHead>
                  <TableHead className="hidden sm:table-cell sm:w-28">
                    {t("marketplaces:channel")}
                  </TableHead>
                  <TableHead className="hidden sm:table-cell sm:w-40">
                    {t("sales:shipTo")}
                  </TableHead>
                  <TableHead className="w-24">{t("marketplaces:status")}</TableHead>
                  <TableHead className="hidden text-right sm:table-cell sm:w-20">
                    {t("sales:items")}
                  </TableHead>
                  <TableHead className="w-24 text-right">{t("sales:total")}</TableHead>
                  <TableHead className="hidden sm:table-cell sm:w-36">
                    {t("sales:ordered")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orderRows.map((o) => (
                  <TableRow
                    key={o.id}
                    className="cursor-pointer"
                    onClick={() => setDetailOrderId(o.id)}
                  >
                    <TableCell>
                      <span className="block truncate font-medium">{o.orderNumber}</span>
                      <span className="block truncate font-mono text-xs text-muted-foreground">
                        {o.externalOrderId}
                      </span>
                      {/* Channel + date collapse into this cell below `sm:`. */}
                      <span className="block truncate text-xs text-muted-foreground sm:hidden">
                        {t(`marketplaces:name.${o.marketplace}`)} · {whenever(o.orderedAt, locale)}
                      </span>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {t(`marketplaces:name.${o.marketplace}`)}
                      <span className="block text-xs text-muted-foreground">{o.accountLabel}</span>
                    </TableCell>
                    <TableCell className="hidden text-sm sm:table-cell">
                      {o.buyerName ?? "—"}
                      {(o.shippingCity || o.shippingState) && (
                        <span className="block text-xs text-muted-foreground">
                          {[o.shippingCity, o.shippingState].filter(Boolean).join(", ")}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[o.status]}>
                        {t(`sales:status.${o.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden text-right tabular-nums sm:table-cell">
                      {integer(o.itemCount, locale)}
                      {o.unmatchedLineCount > 0 && (
                        <span className="ml-1.5 inline-block">
                          <Badge variant="warning">{o.unmatchedLineCount}</Badge>
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {money(o.total, o.currency, locale)}
                    </TableCell>
                    <TableCell className="hidden text-xs text-muted-foreground sm:table-cell">
                      {whenever(o.orderedAt, locale)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {orderRows.length >= limit && (
              <div className="flex justify-center border-t p-3">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setLimit((n) => n + 50)}
                >
                  {t("marketplaces:loadMore")}
                </Button>
              </div>
            )}
          </Card>
        )}
      </section>

      <Dialog
        open={detailOrderId !== null}
        onOpenChange={(open) => !open && setDetailOrderId(null)}
      >
        <DialogContent className="max-w-2xl">
          {detailQ.isLoading && (
            <p className="text-sm text-muted-foreground">{t("common:loading")}</p>
          )}
          {detailQ.data && (
            <>
              <DialogHeader>
                <DialogTitle>{detailQ.data.orderNumber}</DialogTitle>
                <DialogDescription>
                  {t(`marketplaces:name.${detailQ.data.marketplace}`)} · {detailQ.data.accountLabel}{" "}
                  · {whenever(detailQ.data.orderedAt, locale)}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
                  <div>
                    <dt className="text-xs text-muted-foreground">{t("marketplaces:status")}</dt>
                    <dd>
                      <Badge variant={STATUS_VARIANT[detailQ.data.status]}>
                        {t(`sales:status.${detailQ.data.status}`)}
                      </Badge>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">{t("sales:total")}</dt>
                    <dd className="font-medium tabular-nums">
                      {money(detailQ.data.total, detailQ.data.currency, locale)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">{t("sales:buyer")}</dt>
                    <dd className="font-medium">{detailQ.data.buyerName ?? "—"}</dd>
                  </div>
                  <div className="col-span-2 sm:col-span-3">
                    <dt className="text-xs text-muted-foreground">{t("sales:shipTo")}</dt>
                    <dd>
                      {[
                        detailQ.data.shippingLine1,
                        detailQ.data.shippingLine2,
                        detailQ.data.shippingCity,
                        detailQ.data.shippingState,
                        detailQ.data.shippingPostalCode,
                        detailQ.data.shippingCountry,
                      ]
                        .filter(Boolean)
                        .join(", ") || "—"}
                    </dd>
                  </div>
                </dl>

                <div>
                  <p className="mb-1 text-xs text-muted-foreground">{t("sales:lineItems")}</p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("inventory:sku")}</TableHead>
                        <TableHead className="text-right">{t("sales:qty")}</TableHead>
                        <TableHead className="text-right">{t("sales:total")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detailQ.data.lineItems.map((li) => (
                        <TableRow key={li.id}>
                          <TableCell>
                            <span className="block text-sm">{li.title}</span>
                            <span className="block font-mono text-xs text-muted-foreground">
                              {li.sku ?? li.channelSku}
                              {!li.productId && (
                                <span className="ml-1.5">
                                  <Badge variant="warning">{t("sales:unmatched")}</Badge>
                                </span>
                              )}
                            </span>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {integer(li.quantity, locale)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {money(li.lineTotal, detailQ.data.currency, locale)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={localSaleDialogOpen} onOpenChange={setLocalSaleDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("sales:local.record")}</DialogTitle>
            <DialogDescription>{t("sales:local.recordHint")}</DialogDescription>
          </DialogHeader>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              setLocalSaleError(null);
              createLocalSaleMutation.mutate();
            }}
          >
            <div>
              <Label htmlFor="ls-date">{t("sales:local.date")}</Label>
              <Input
                id="ls-date"
                type="date"
                value={localSaleForm.soldAt}
                onChange={(e) => setLocalSaleForm((f) => ({ ...f, soldAt: e.target.value }))}
                className="mt-1"
                required
              />
            </div>
            <div>
              <Label htmlFor="ls-desc">{t("sales:local.item")}</Label>
              <Input
                id="ls-desc"
                value={localSaleForm.description}
                onChange={(e) => setLocalSaleForm((f) => ({ ...f, description: e.target.value }))}
                className="mt-1"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ls-qty">{t("sales:local.quantity")}</Label>
                <Input
                  id="ls-qty"
                  type="number"
                  min={0}
                  step="1"
                  value={localSaleForm.quantity}
                  onChange={(e) => setLocalSaleForm((f) => ({ ...f, quantity: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="ls-amount">{t("sales:local.amount")}</Label>
                <Input
                  id="ls-amount"
                  type="number"
                  min={0}
                  step="0.01"
                  value={localSaleForm.amount}
                  onChange={(e) => setLocalSaleForm((f) => ({ ...f, amount: e.target.value }))}
                  className="mt-1"
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="ls-soldby">{t("sales:local.soldBy")}</Label>
              <Input
                id="ls-soldby"
                value={localSaleForm.soldBy}
                onChange={(e) => setLocalSaleForm((f) => ({ ...f, soldBy: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="ls-notes">{t("sales:local.notes")}</Label>
              <Textarea
                id="ls-notes"
                value={localSaleForm.notes}
                onChange={(e) => setLocalSaleForm((f) => ({ ...f, notes: e.target.value }))}
                className="mt-1"
                rows={2}
              />
            </div>
            {localSaleError && <p className="text-sm text-destructive">{localSaleError}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setLocalSaleDialogOpen(false)}>
                {t("invoices:form.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={
                  !localSaleForm.description.trim() ||
                  !localSaleForm.amount ||
                  createLocalSaleMutation.isPending
                }
              >
                {createLocalSaleMutation.isPending
                  ? t("sales:local.saving")
                  : t("sales:local.save")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** `null` (no prior-period data to compare against) is deliberately distinct
 * from `0` (flat) — a brand-new item or a period with no history yet isn't
 * "unchanged," it just has nothing to measure against. */
function pctChange(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

function formatTrendDate(isoDate: string, locale: string): string {
  const d = new Date(`${isoDate}T00:00:00`);
  return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" }).format(d);
}

function KpiCard({
  label,
  value,
  changePct,
}: {
  label: string;
  value: string;
  changePct: number | null;
}) {
  const { t } = useTranslation();
  const rounded = changePct === null ? null : Math.round(changePct * 10) / 10;
  const Icon = rounded === null || rounded === 0 ? Minus : rounded > 0 ? TrendingUp : TrendingDown;
  const tone =
    rounded === null || rounded === 0
      ? "text-muted-foreground"
      : rounded > 0
        ? "text-[hsl(var(--success))]"
        : "text-destructive";
  return (
    <Card>
      <CardHeader className="p-3 pb-1">
        <CardTitle className="text-xs font-normal text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent className="flex items-end justify-between p-3 pt-0">
        <span className="text-2xl font-semibold tabular-nums">{value}</span>
        <span className={cn("flex items-center gap-1 text-xs font-medium tabular-nums", tone)}>
          <Icon className="h-3.5 w-3.5" aria-hidden />
          {rounded === null ? t("sales:kpi.noPriorPeriod") : `${rounded > 0 ? "+" : ""}${rounded}%`}
        </span>
      </CardContent>
    </Card>
  );
}

function TrendTooltip({
  active,
  payload,
  locale,
}: {
  active?: boolean;
  payload?: Array<{ payload: KpiSeriesPoint }>;
  locale: string;
}) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <div className="rounded-md border bg-popover p-2 text-xs shadow-md">
      <p className="mb-1 font-medium">{formatTrendDate(point.date, locale)}</p>
      <p className="text-muted-foreground">
        {money(point.revenue, "USD", locale)} · {integer(point.orders, locale)}{" "}
        {point.orders === 1 ? "order" : "orders"}
      </p>
    </div>
  );
}
